import { Button as LegacyButton, Input, Select } from '@evaneos/b2b-ui/legacy';
import { Button } from '@evaneos/b2b-ui';
import cx from 'classnames';
import { ChangeEvent, ReactNode, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { DateRangeFromInputs } from '../../../../../../design-system/Form/DateRangeFromInputs';
import { DateRange } from '../../../../../../design-system/Form/types';
import { Add } from '../../../../../../design-system/Icons/Add';
import { Close } from '../../../../../../design-system/Icons/Close';
import { Option } from '../../../../../../design-system/types';
import { SidePanelViewMode } from '../../../../SidePanel/types';
import { getLegalInformationFormattedForSelect } from '../../../../../../resources/contract/selectors';
import {
    ContractEditStep,
    ContractId,
    TravelInformation,
} from '../../../../../../resources/contract/types';
import { useAppSelector } from '../../../../../../shared/redux';
import { useGetContractTravelInformation } from '../../../../../../resources/contract/hooks';
import styleContract from '../../../Contract.css';
import styleStepButtons from '../StepButtons.css';

import s from './TravelInformationStep.css';

type Props = {
    contractId: ContractId;
    saveTravelInformation: (travelInformation: TravelInformation) => void;
    onCancel?: () => void;
    viewMode?: SidePanelViewMode;
};

enum ProposalType {
    // TIPI = 'tipi',
    ONLINE = 'online',
    UPLOAD = 'upload',
}

export const TravelInformationStep = ({
    saveTravelInformation,
    contractId,
    onCancel,
    viewMode,
}: Props): JSX.Element => {
    const intl = useIntl();

    const travelInformation = useGetContractTravelInformation(contractId);

    const legalInformationOptions = useAppSelector(getLegalInformationFormattedForSelect);

    const PROPOSAL_OPTIONS = [
        {
            label: intl.formatMessage({
                id: 'contract.travel_information.online_proposal',
            }),
            value: ProposalType.ONLINE,
        },
        // {
        //     label: intl.formatMessage({id: "contract.travel_information.tipi_proposal"}),
        //     value: ProposalType.TIPI,
        // },
        {
            label: intl.formatMessage({
                id: 'contract.travel_information.attachment_proposal',
            }),
            value: ProposalType.UPLOAD,
        },
    ];

    const [arrivalDate, setArrivalDate] = useState<Date | undefined>(
        travelInformation?.arrivalDate,
    );
    const [departureDate, setDepartureDate] = useState<Date | undefined>(
        travelInformation?.departureDate,
    );
    const [proposalUrl, setProposalUrl] = useState<string | File | URL>(
        travelInformation?.proposal ?? '',
    );
    const [selectedProposalType, setSelectedProposalType] = useState<Option>(PROPOSAL_OPTIONS[0]);
    const [selectedLegalInformation, setSelectedLegalInformation] = useState<Option | undefined>(
        () =>
            legalInformationOptions.find(
                (legal) => legal.value === travelInformation?.legalInformationReference,
            ),
    );
    const [prevLegalRef, setPrevLegalRef] = useState(travelInformation?.legalInformationReference);
    const [prevLegalOptions, setPrevLegalOptions] = useState(legalInformationOptions);

    const currentLegalRef = travelInformation?.legalInformationReference;
    if (prevLegalRef !== currentLegalRef || prevLegalOptions !== legalInformationOptions) {
        setPrevLegalRef(currentLegalRef);
        setPrevLegalOptions(legalInformationOptions);
        setSelectedLegalInformation(
            legalInformationOptions.find((legal) => legal.value === currentLegalRef),
        );
    }

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleDateRangeChange = (dateRange: DateRange): void => {
        if (dateRange.from) {
            setArrivalDate(dateRange.from);
        }
        if (dateRange.to) {
            setDepartureDate(dateRange.to);
        }
    };

    const handleProposalOnChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setProposalUrl(value);
    };

    const handleLegalInformationOnChange = (selected: unknown): void => {
        if (!selected) {
            return;
        }
        setSelectedLegalInformation(selected as Option);
    };

    function isValidUrl() {
        try {
            if (proposalUrl !== '') {
                return Boolean(new URL(proposalUrl as URL | string));
            }
            return true;
        } catch {
            return false;
        }
    }

    const isValid =
        !!departureDate &&
        !!arrivalDate &&
        departureDate >= arrivalDate &&
        (selectedProposalType.value !== ProposalType.ONLINE ||
            (proposalUrl !== '' && isValidUrl())) &&
        (selectedProposalType.value !== ProposalType.UPLOAD || !!proposalUrl) &&
        !!selectedLegalInformation;

    const onDrop = (acceptedFiles: File[]) => {
        setProposalUrl(acceptedFiles[0]);
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
        },
        multiple: false,
        noDrag: true,
        useFsAccessApi: false,
        onDrop,
    });

    const submit = (): void => {
        if (!arrivalDate || !departureDate) {
            return;
        }

        setIsSubmitting(true);
        // save to parent component's state
        saveTravelInformation({
            proposal: proposalUrl,
            arrivalDate,
            departureDate,
            legalInformationReference: selectedLegalInformation && selectedLegalInformation.value,
        });
        setIsSubmitting(false);
    };

    return (
        <div
            className={s.travelInformationContainer}
            {...(viewMode && { 'data-contract-view': viewMode })}
        >
            <p className={styleContract.stepSubtitle}>
                <FormattedMessage
                    id="contract.step_subtitle"
                    values={{
                        step: ContractEditStep.TRAVEL_INFORMATION,
                    }}
                />
            </p>
            <h1 className="title-m">
                <FormattedMessage id="contract.travel_information.title" />
            </h1>
            <div className={s.datesForm}>
                <div className={cx(s.inputWrapper, s.selectLegalInformation)}>
                    <label className={s.labelSelect} htmlFor="select-legal-information">
                        <FormattedMessage id="contract.travel_information.legal_information" />
                    </label>
                    <Select
                        inputId="select-legal-information"
                        options={legalInformationOptions}
                        value={selectedLegalInformation}
                        onChange={handleLegalInformationOnChange}
                    />
                </div>
                <DateRangeFromInputs
                    initialStartDate={arrivalDate}
                    initialEndDate={departureDate}
                    onDateRangeChange={handleDateRangeChange}
                    startDateLabel={intl.formatMessage({
                        id: 'contract.travel_information.arrival_date',
                    })}
                    endDateLabel={intl.formatMessage({
                        id: 'contract.travel_information.departure_date',
                    })}
                    inputWrapperClassName={s.inputWrapper}
                />
                <div className={s.europeanDirectiveMessageContainer}>
                    <div className={s.europeanDirectiveMessage}>
                        <h3>
                            <FormattedMessage id="contract.travel_information.european_directive.title" />
                        </h3>
                        <p>
                            <FormattedMessage
                                id="contract.travel_information.european_directive.message"
                                values={{
                                    a: (chunks: ReactNode) => (
                                        <a
                                            className={s.legalInformationLink}
                                            target="_blank"
                                            href={`/agency-legal-information`}
                                            rel="noopener noreferrer"
                                        >
                                            {chunks}
                                        </a>
                                    ),
                                }}
                            />
                        </p>
                    </div>
                </div>
            </div>

            <div className={s.quoteForm}>
                <h3>
                    <FormattedMessage id="contract.travel_information.shared_proposal" />
                </h3>
                <div className={s.quoteInputsContainer}>
                    <div className={s.quoteInputsContainerLeft}>
                        <label htmlFor="select-proposal-type" className={s.label}>
                            <FormattedMessage id="contract.travel_information.proposal_type" />
                        </label>
                        <Select
                            id="select-proposal-type"
                            options={PROPOSAL_OPTIONS}
                            value={selectedProposalType}
                            onChange={(selected) =>
                                selected && setSelectedProposalType(selected as Option)
                            }
                        />
                    </div>
                    <div className={s.quoteInputsContainerRight}>
                        {selectedProposalType.value === ProposalType.ONLINE && (
                            <Input
                                id="input-proposal-url"
                                data-testid="input-proposal-url"
                                name="input-proposal-url"
                                placeholder="https://www.example.com"
                                onChange={handleProposalOnChange}
                                type="url"
                                label={intl.formatMessage({
                                    id: 'contract.travel_information.proposal_url',
                                })}
                                className={s.quoteInput}
                                value={typeof proposalUrl === 'string' ? proposalUrl : undefined}
                                error={!isValidUrl()}
                                errorText={intl.formatMessage({
                                    id: 'contract.travel_information.proposal_url_error',
                                })}
                            />
                        )}

                        {selectedProposalType.value === ProposalType.UPLOAD &&
                            (proposalUrl instanceof File ? (
                                <div className={s.proposalFile}>
                                    <p>{proposalUrl.name}</p>
                                    <button
                                        type="button"
                                        className={s.deleteButton}
                                        onClick={() => setProposalUrl('')}
                                    >
                                        <Close color="var(--ink)" />
                                    </button>
                                </div>
                            ) : (
                                <div {...getRootProps()}>
                                    <input id="upload-proposal" {...getInputProps()} />
                                    <LegacyButton
                                        priority="secondary"
                                        onClick={(e) => e.preventDefault()}
                                        className={s.uploadProposalButton}
                                    >
                                        <Add color="var(--ink)" />
                                        <FormattedMessage id="contract.travel_information.add_proposal" />
                                    </LegacyButton>
                                </div>
                            ))}
                    </div>
                </div>
            </div>

            <div
                className={styleStepButtons.buttonContainer}
                {...(viewMode && { 'data-contract-view': viewMode })}
            >
                {onCancel ? (
                    <Button
                        variant="outlined"
                        type="button"
                        onClick={onCancel}
                        className={cx(styleStepButtons.backButton, s.backButton)}
                    >
                        <FormattedMessage id="form.cancel" />
                    </Button>
                ) : (
                    <Button
                        component={Link}
                        variant="outlined"
                        replace
                        to={{
                            pathname: `/dossier/${contractId}/contract`,
                        }}
                        className={cx(styleStepButtons.backButton, s.backButton)}
                    >
                        <FormattedMessage id="form.cancel" />
                    </Button>
                )}

                <Button
                    onClick={submit}
                    disabled={!isValid || isSubmitting}
                    className={styleStepButtons.buttonValidate}
                    data-testid="button-submit-next"
                >
                    <FormattedMessage id="contract.next_step" />
                </Button>
            </div>
        </div>
    );
};
